// /src/theme.js
import { createGlobalStyle } from 'styled-components';

export const lightTheme = {
  name: 'light',
  bg: '#F5F7FB',
  card: '#FFFFFF',
  fg: '#0F1115',
  muted: '#6b7280',
  border: '#E6E9F1',
  primary: '#b08d57',
  primaryFg: '#1a1a1a',
  shadow: '0 10px 30px rgba(16,24,40,.08)',
  radius: '18px',
  glass: 'rgba(255,255,255,.75)',

  // типографика
  fontSans:
    "Manrope, Inter, 'SF Pro Text', -apple-system, 'Segoe UI', Roboto, Ubuntu, Cantarell, 'Helvetica Neue', Arial, system-ui, sans-serif",
};

export const darkTheme = {
  name: 'dark',
  bg: '#0A0C12',
  card: '#121318',
  fg: '#ecbf79',
  muted: '#9ca3af',
  border: '#20222A',
  primary: '#d0b47a',
  primaryFg: '#0b0c10',
  shadow: '0 12px 34px rgba(0,0,0,.45)',
  radius: '18px',
  glass: 'rgba(12,13,18,.6)',

  fontSans:
    "Manrope, Inter, 'SF Pro Text', -apple-system, 'Segoe UI', Roboto, Ubuntu, Cantarell, 'Helvetica Neue', Arial, system-ui, sans-serif",
};

// Глобальные стили: крупнее текст, аккуратные заголовки, единые размеры элементов формы
export const GlobalStyles = createGlobalStyle`
  :root{
    /* цвета/тени/радиус */
    --bg:        ${({ theme }) => theme.bg};
    --card:      ${({ theme }) => theme.card};
    --fg:        ${({ theme }) => theme.fg};
    --muted:     ${({ theme }) => theme.muted};
    --bdr:       ${({ theme }) => theme.border};
    --primary:   ${({ theme }) => theme.primary};
    --primaryFg: ${({ theme }) => theme.primaryFg};
    --shadow:    ${({ theme }) => theme.shadow};
    --radius:    ${({ theme }) => theme.radius};
    --glass:     ${({ theme }) => theme.glass};

    /* золотой градиент */
    --gold: linear-gradient(to right, #ecbf79, #c7924c);
    --radius:    ${({ theme }) => theme.radius};
    --glass:     ${({ theme }) => theme.glass};

    /* шрифты и размеры */
    --font-sans: ${({ theme }) => theme.fontSans};

    /* Крупнее, адаптивные размеры через clamp */
    --text-sm:  clamp(14px, 0.18vw + 13px, 15px);
    --text-md:  clamp(16px, 0.40vw + 15px, 19px);   /* базовый размер */
    --text-lg:  clamp(18px, 0.70vw + 16px, 22px);
    --text-xl:  clamp(20px, 1.00vw + 18px, 26px);
    --text-2xl: clamp(24px, 1.60vw + 20px, 32px);
    --text-3xl: clamp(28px, 2.40vw + 22px, 40px);

    /* высота строки */
    --lh-text: 1.6;
    --lh-head: 1.25;

    /* отступы для интерактивных элементов (немного больше) */
    --ctl-pad-y: 12px;
    --ctl-pad-x: 14px;
  }

  /* Сбросы и базовая типографика */
  *, *::before, *::after { box-sizing: border-box; }
  html, body, #root { height: 100%; }
  body{
    margin: 0;
    background: var(--bg);
    color: var(--fg);
    font-family: var(--font-sans);
    font-size: var(--text-md);
    line-height: var(--lh-text);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  ::selection{
    background: ${({ theme }) => theme.primary}33;
    color: var(--fg);
  }

  /* Заголовки заметно крупнее */
  h1, h2, h3, h4, h5, h6{
    margin: 0 0 0.6em;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--fg);
  }
  h1{ font-size: var(--text-3xl); line-height: var(--lh-head); }
  h2{ font-size: var(--text-2xl); line-height: var(--lh-head); }
  h3{ font-size: var(--text-xl);  line-height: var(--lh-head); }
  h4{ font-size: var(--text-lg);  line-height: var(--lh-head); }

  p{ margin: 0 0 0.9em; }
  .muted, .text-muted{ color: var(--muted); }
  small, .text-sm{ font-size: var(--text-sm); }

  a{ color: inherit; text-decoration: none; }
  img{ max-width: 100%; display: block; }

  /* Формы и кнопки — единый крупный размер */
  input, textarea, select, button{
    font: inherit;
    font-size: var(--text-md); /* >=16px — без зума на iOS */
    line-height: 1.5;
  }
  input, textarea, select{
    background: var(--card);
    color: var(--fg);
    border: 1px solid var(--bdr);
    border-radius: var(--radius);
    padding: var(--ctl-pad-y) var(--ctl-pad-x);
    outline: none;
  }
  textarea{ min-height: 120px; resize: vertical; }

  button, .btn{
    cursor: pointer;
    border: 1px solid var(--bdr);
    background: var(--card);
    color: var(--fg);
    border-radius: var(--radius);
    padding: calc(var(--ctl-pad-y) - 2px) calc(var(--ctl-pad-x) + 2px);
    font-weight: 700;
    box-shadow: var(--shadow);
    transition: transform .04s ease, box-shadow .2s ease, background .2s ease, color .2s ease;
  }
  .btn-primary, button.primary{
    background: var(--primary);
    color: var(--primaryFg);
    border-color: transparent;
  }
  button:active, .btn:active{ transform: translateY(1px); }

  /* Карточки по умолчанию — те же скругления/тень */
  .card{
    background: var(--card);
    border: 1px solid var(--bdr);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
  }

  /* Утилита контейнера (если где-то нет своей) */
  .container{
    width: min(1400px, 100vw);
    margin: 0 auto;
    padding-left: max(clamp(12px, 4vw, 18px), env(safe-area-inset-left));
    padding-right: max(clamp(12px, 4vw, 18px), env(safe-area-inset-right));
  }
`;

export default lightTheme;
